import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import MainCard from 'components/MainCard';
import useGet from 'hooks/useGet';
import apiClient from 'utils/apiClient';

import {
  formatCurrency,
  formatDateTime,
  normalizeOrder,
  resolveProductImage,
  toTitleCase
} from './helpers';

const paymentStatuses = [
  'created',
  'pending',
  'authorized',
  'captured',
  'failed',
  'cancelled',
  'refunded'
];

export default function CustomProductPaymentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  const { data, error, loading, mutate } = useGet(
    id ? `/api/admin/custom-product-payments/orders/${id}` : null
  );

  const order = useMemo(
    () => normalizeOrder(data?.order || data?.data || data || {}),
    [data]
  );

  const currentStatus = String(order.paymentStatus || 'pending').toLowerCase();
  const effectiveStatus = selectedStatus || currentStatus;
  const image = resolveProductImage(order.customProduct);

  const onUpdateStatus = async () => {
    if (!id || !effectiveStatus) return;

    setUpdating(true);
    setActionError('');
    setActionSuccess('');

    try {
      await apiClient.patch(`/api/admin/custom-product-payments/orders/${id}/payment-status`, {
        paymentStatus: effectiveStatus
      });
      setActionSuccess('Custom product payment status updated successfully.');
      await mutate();
    } catch (requestError) {
      setActionError(
        requestError?.response?.data?.message || 'Failed to update custom product payment status.'
      );
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <Typography>Loading custom product payment details...</Typography>;
  }

  if (error) {
    return <Alert severity="error">Failed to load custom product payment details.</Alert>;
  }

  if (!order.id) {
    return <Alert severity="warning">Custom product order not found.</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Typography variant="h4">Custom Product Payment Details</Typography>
        <Button variant="outlined" onClick={() => navigate('/custom-products/payments')}>
          Back to Payments
        </Button>
      </Stack>

      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      {actionSuccess ? <Alert severity="success">{actionSuccess}</Alert> : null}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <MainCard title="Order Summary">
            <Stack spacing={1.1}>
              <Typography><strong>Order ID:</strong> {order.id}</Typography>
              <Typography><strong>Product:</strong> {order.customProduct?.title || '-'}</Typography>
              <Typography><strong>SKU:</strong> {order.customProduct?.SKU || '-'}</Typography>
              <Typography><strong>Customer:</strong> {order.customerName || '-'}</Typography>
              <Typography><strong>Email:</strong> {order.customerEmail || '-'}</Typography>
              <Typography><strong>Payment Status:</strong> {toTitleCase(order.paymentStatus)}</Typography>
              <Typography><strong>Currency:</strong> {order.currency}</Typography>
              <Typography>
                <strong>Total:</strong> {formatCurrency(order.currency, order.totalAmount)}
              </Typography>
              <Typography><strong>Created:</strong> {formatDateTime(order.createdAt)}</Typography>
              <Typography><strong>Updated:</strong> {formatDateTime(order.updatedAt)}</Typography>
            </Stack>
          </MainCard>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <MainCard title="Product Preview">
            <Stack spacing={1.5}>
              {image ? (
                <img
                  src={image}
                  alt={order.customProduct?.title || 'Custom product'}
                  style={{
                    width: '100%',
                    maxHeight: 240,
                    objectFit: 'cover',
                    borderRadius: 12
                  }}
                />
              ) : null}
              <Typography variant="subtitle1">{order.customProduct?.title || 'Custom product'}</Typography>
              <Typography color="text.secondary">
                {order.customProduct?.description || 'No product description available.'}
              </Typography>
            </Stack>
          </MainCard>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <MainCard title="Update Payment Status">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                select
                size="small"
                label="Payment Status"
                value={effectiveStatus}
                onChange={(event) => setSelectedStatus(event.target.value)}
                sx={{ minWidth: 240 }}
              >
                {paymentStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {toTitleCase(status)}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="contained" onClick={onUpdateStatus} disabled={updating}>
                {updating ? 'Updating...' : 'Update'}
              </Button>
            </Stack>
          </MainCard>
        </Grid>
      </Grid>
    </Stack>
  );
}
